import { useState } from 'react'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type { ServerDetail } from '../api/client'

export const Route = createFileRoute('/servers/$slug')({
  component: ServerDetailPage,
})

function ServerDetailPage() {
  const { slug } = useParams({ from: '/servers/$slug' })
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['server', slug],
    queryFn: () => api<ServerDetail>(`/api/servers/${slug}`),
  })

  const [values, setValues] = useState<Record<string, string>>({})
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api<ServerDetail>(`/api/servers/${slug}/config`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setSavedAt(Date.now())
      qc.invalidateQueries({ queryKey: ['servers'] })
      qc.invalidateQueries({ queryKey: ['server', slug] })
    },
  })

  if (isLoading) return <div className="text-sm text-zinc-500">Loading…</div>
  if (error) return <div className="text-sm text-red-600">{(error as Error).message}</div>
  if (!data) return null

  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-zinc-900">{data.name}</h1>
        <p className="mt-1 text-sm text-zinc-600">{data.description}</p>
      </header>

      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <h2 className="text-lg font-medium text-zinc-900">Configuration</h2>
        <p className="mt-1 text-sm text-zinc-600">
          Secrets are stored encrypted in Google Secret Manager. They are never displayed back after
          saving.
        </p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate(values)
          }}
        >
          {data.configSchema.map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-zinc-700">{f.label}</label>
              <input
                type={f.type === 'secret' ? 'password' : 'text'}
                required={f.required}
                placeholder={f.placeholder ?? ''}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                className="mt-1 block w-full rounded-md border border-zinc-300 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={save.isPending}
            className="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          {savedAt && <span className="ml-3 text-sm text-emerald-600">Saved.</span>}
        </form>
      </section>

      <section className="rounded-lg border border-zinc-200 bg-white p-6">
        <h2 className="text-lg font-medium text-zinc-900">Add to Claude</h2>
        <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm text-zinc-700">
          <li>
            In Claude Desktop, open <strong>Settings → Connectors → Add custom connector</strong>.
          </li>
          <li>
            Paste the URL: <code className="rounded bg-zinc-100 px-1.5 py-0.5">{data.mcpUrl}</code>
          </li>
          <li>
            Open <strong>Advanced settings</strong> and paste the OAuth Client ID:{' '}
            <code className="rounded bg-zinc-100 px-1.5 py-0.5">{data.oauthClientId}</code>
          </li>
          <li>Click Add. Sign in with your Lab Digital Microsoft account when prompted.</li>
        </ol>
      </section>
    </div>
  )
}
